import { useQueryClient } from '@tanstack/react-query';
import { Download, Filter, Loader2, RefreshCw, Search, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Input } from '../../components/ui/input';
import { toast } from '../../components/ui/use-toast';
import { services } from '../../services';
import { UserStatus } from '../../types/user';

interface UsersFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: UserStatus[];
  onStatusFilterChange: (statuses: UserStatus[]) => void;
  selectedCount: number;
  onBulkApprove: () => void;
  onExport: () => void;
  onAddUser: () => void;
  onRefresh?: () => void;
}

// Only these exist in the backend user status enum (helper/enum.ts).
// The previous extra options (KYC_SUBMITTED, KYC_VERIFIED, ACCREDITED, ACTIVE,
// SUSPENDED) were never valid and always returned zero results.
const allStatuses: UserStatus[] = ['PENDING', 'APPROVED', 'REJECTED'];

export function UsersFilters({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  selectedCount,
  onBulkApprove,
  onExport,
  onAddUser,
  onRefresh,
}: UsersFiltersProps) {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const toggleStatus = (status: UserStatus) => {
    if (statusFilter.includes(status)) {
      onStatusFilterChange(statusFilter.filter((s) => s !== status));
    } else {
      onStatusFilterChange([...statusFilter, status]);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Invalidate and refetch users data
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      onRefresh?.();
      toast({ description: 'Data refreshed successfully', variant: 'default' });
    } catch (error) {
      toast({ description: 'Failed to refresh data', variant: 'destructive' });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Fetch all users for export
      interface UserExportData {
        name?: string | null;
        email?: string | null;
        walletAddress?: string;
        status?: string;
        role?: {
          roleName?: string;
        };
        createdAt?: string;
      }

      interface UserListResponse {
        data: {
          data: UserExportData[];
        };
      }
      const response = await services.get<UserListResponse>('/user/list?pagination=false');

      const usersArray = response?.data?.data?.data || response?.data?.data;
      if (usersArray && Array.isArray(usersArray)) {
        const users = usersArray;

        // Convert to CSV
        const headers = ['Name', 'Email', 'Wallet Address', 'Status', 'Role', 'Created At'];
        const rows = users.map((user: UserExportData) => [
          user.name || '',
          user.email || '',
          user.walletAddress || '',
          user.status || '',
          user.role?.roleName || '',
          user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '',
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map((row: string[]) => row.map((cell) => `"${cell}"`).join(',')),
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({ description: 'Users exported successfully', variant: 'default' });
        onExport();
      } else {
        throw new Error('No data to export');
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { status?: number; data?: { message?: string } } };

      if (axiosError?.response?.status === 401 || axiosError?.response?.status === 403) {
        toast({ description: 'Unauthorized Request', variant: 'destructive' });
        localStorage.removeItem('token');
      } else {
        toast({
          description: axiosError?.response?.data?.message || 'Failed to export users',
          variant: 'destructive',
        });
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className='flex flex-col gap-3 sm:gap-4'>
      {/* All Filters in One Row */}
      <div className='flex flex-col sm:flex-row gap-3 items-stretch sm:items-center'>
        {/* Search */}
        <div className='relative flex-1 min-w-0 max-w-sm'>
          <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
          <Input
            placeholder='Search by name, email, or wallet...'
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className='pl-10 w-full'
          />
        </div>

        {/* Filter and Refresh */}
        <div className='flex gap-2 items-center flex-shrink-0'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='outline' className='gap-2'>
                <Filter className='w-4 h-4' />
                <span className='hidden sm:inline'>Filter</span>
                {statusFilter.length > 0 && (
                  <span className='ml-1 px-1.5 py-0.5 text-xs rounded-full bg-primary text-primary-foreground'>
                    {statusFilter.length}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='start' className='w-56'>
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {allStatuses.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={statusFilter.includes(status)}
                  onCheckedChange={() => toggleStatus(status)}
                >
                  {status.replace(/_/g, ' ')}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant='ghost'
            size='icon'
            onClick={handleRefresh}
            disabled={isRefreshing}
            className='flex-shrink-0'
          >
            {isRefreshing ? (
              <Loader2 className='w-4 h-4 animate-spin' />
            ) : (
              <RefreshCw className='w-4 h-4' />
            )}
          </Button>
        </div>

        {/* Spacer */}
        <div className='hidden sm:block flex-1' />

        {/* Export and Add User - pushed to the end */}
        <div className='flex gap-2 items-center flex-shrink-0'>
          <Button
            variant='outline'
            size='sm'
            onClick={handleExport}
            disabled={isExporting}
            className='gap-2'
          >
            {isExporting ? (
              <Loader2 className='w-4 h-4 animate-spin' />
            ) : (
              <Download className='w-4 h-4' />
            )}
            <span className='hidden sm:inline'>Export</span>
          </Button>

          <Button variant='default' size='sm' className='gap-2' onClick={onAddUser}>
            <UserPlus className='w-4 h-4' />
            <span className='hidden sm:inline'>Add User</span>
            <span className='sm:hidden'>Add</span>
          </Button>
        </div>
      </div>

      {/* Bulk Actions (shown only when users selected) */}
      {selectedCount > 0 && (
        <div className='flex items-center gap-2 flex-wrap'>
          <span className='text-sm text-muted-foreground whitespace-nowrap'>
            {selectedCount} selected
          </span>
          <Button variant='default' size='sm' onClick={onBulkApprove} className='whitespace-nowrap'>
            Bulk Approve
          </Button>
        </div>
      )}
    </div>
  );
}
